// ─── settingsSync: cross-device persistence over D1, one owner ───
//
// The contract: localStorage stays the source the app READS (existing
// consumers like OrderflowTrail poll `solcheddar_watchlist` as a BARE
// ARRAY — the stored format must never grow an envelope). This module
// only decides WHICH value localStorage holds, by racing timestamps
// against the server:
//
//   per-key last-write-wins. Whole value, no field merging — two devices
//   editing the same list offline resolve to whichever saved later, a rule
//   dumb enough to always explain what happened. Timestamps live in a
//   separate meta key (`solcheddar_sync_meta`), not in the values.
//
// Server: GET/PUT /api/settings (Pages Function, session cookie = identity;
// email never travels in the body). PUT upserts only where the incoming
// updated_at is newer, so a stale tab can't clobber a phone's later write.
//
// Failure posture: everything degrades to local-only. 401 = not signed in,
// 404 = Function not deployed yet, network down = trade anyway. Sync is a
// convenience layered on the app, never a gate in front of it.

const META_KEY = 'solcheddar_sync_meta'
const API = '/api/settings'
const PUSH_DEBOUNCE_MS = 2000
// Hard ceiling per key, on the RAW value (before the {v,data} envelope).
// This is our cap, not Cloudflare's. D1 stores the bound parameter up to
// the 2MB row/string limit; the 100KB SQL-statement limit does not apply
// to bound values. Workers request bodies allow 100MB. /api/settings
// checks the envelope string against its own MAX_VALUE_BYTES (1MB).
// 900KB leaves room for that envelope and still fits several full groups
// of enriched tokens (~3KB each). Over the cap: local-only, never retried.
const MAX_VALUE_BYTES = 900_000
const VERSION = 1

const state = {
  meta: {},            // key -> updated_at (ms) of the local value
  lastApplied: {},     // key -> JSON string last written by THIS module (echo guard)
  pending: new Map(),  // key -> { value, updated_at } queued for push
  pushTimer: null,
  onRemote: null,      // (key, value) => void — lets App update React state
  keys: [],            // [{ key, storageKey }]
  tooBig: new Set(),   // keys refused by the size cap — warn once each
  dead: new Set(),     // keys the server rejected 4xx — never retried
  started: false,
}

function loadMeta() {
  try { state.meta = JSON.parse(localStorage.getItem(META_KEY)) || {} } catch { state.meta = {} }
}
function saveMeta() {
  try { localStorage.setItem(META_KEY, JSON.stringify(state.meta)) } catch {}
}

/**
 * Called by the state owner (App) whenever a synced value changes. Stamps
 * now, persists meta, debounces the push. Skips if the value is one this
 * module itself just applied from remote — without that guard every pull
 * would echo straight back as a push with a fresh (wrong) timestamp.
 */
export function queuePush(key, value) {
  const json = JSON.stringify(value)
  if (state.lastApplied[key] === json) return
  if (json.length > MAX_VALUE_BYTES) {
    // Degrade to local-only for THIS key. Everything still works; it just
    // stops crossing devices. Marking lastApplied means we complain once per
    // distinct value, not once per render.
    state.lastApplied[key] = json
    if (!state.tooBig.has(key)) {
      state.tooBig.add(key)
      console.warn(`[settingsSync] "${key}" is ${json.length}B — over the ${MAX_VALUE_BYTES}B sync cap. Local-only from here; trim it to resume cross-device sync.`)
    }
    return
  }
  state.tooBig.delete(key)
  state.lastApplied[key] = json
  const now = Date.now()
  state.meta[key] = now
  saveMeta()
  state.pending.set(key, { value: json, updated_at: now })
  clearTimeout(state.pushTimer)
  state.pushTimer = setTimeout(flushPush, PUSH_DEBOUNCE_MS)
}

async function flushPush() {
  if (state.pending.size === 0) return
  const batch = []
  for (const [key, p] of state.pending) {
    batch.push({ key, value: JSON.stringify({ v: VERSION, data: JSON.parse(p.value) }), updated_at: p.updated_at })
  }
  state.pending.clear()
  try {
    const r = await fetch(API, {
      method: 'PUT',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items: batch }),
    })
    if (!r.ok) {
      // 4xx IS TERMINAL. The server is rejecting the PAYLOAD, not failing to
      // receive it — retrying sends the identical bytes and gets the identical
      // answer. The old catch could not tell a 400 from a dropped connection,
      // so one oversized key became an endless timer chain: queuePush resets
      // the 2s debounce on every change, flushPush 400s, requeues, repeats.
      //
      // 401 (signed out) and 404 (Function not deployed) are expected states,
      // not errors — the module's whole posture is local-only when sync is
      // unavailable. Drop quietly for those; warn once for a real rejection.
      if (r.status >= 400 && r.status < 500) {
        for (const b of batch) state.dead.add(b.key)
        if (r.status !== 401 && r.status !== 404) {
          console.warn(`[settingsSync] PUT rejected ${r.status} for [${batch.map(b => b.key).join(', ')}] — dropped, not retried. Payload is bad, not the network.`)
        }
        return
      }
      throw new Error(`HTTP ${r.status}`)
    }
    // A successful push clears any earlier rejection for those keys.
    for (const b of batch) state.dead.delete(b.key)
  } catch {
    // 5xx or network only. Requeue once under the same timestamps — LWW makes
    // retries idempotent and a NEWER local edit meanwhile just overwrites the
    // pending entry.
    for (const b of batch) {
      if (!state.pending.has(b.key)) {
        const env = JSON.parse(b.value)
        state.pending.set(b.key, { value: JSON.stringify(env.data), updated_at: b.updated_at })
      }
    }
    clearTimeout(state.pushTimer)
    state.pushTimer = setTimeout(flushPush, 30_000)
  }
}

/** Unwrap {v, data} envelopes; tolerate future versions by taking .data. */
function unwrap(raw) {
  try {
    const env = JSON.parse(raw)
    if (env && typeof env === 'object' && 'v' in env && 'data' in env) return env.data
    return env   // pre-envelope row — accept as-is
  } catch { return undefined }
}

async function pull() {
  let rows
  try {
    const r = await fetch(API, { credentials: 'include' })
    if (!r.ok) return        // 401 signed-out / 404 not deployed — local-only
    rows = (await r.json())?.items
  } catch { return }
  if (!Array.isArray(rows)) return
  for (const row of rows) {
    const spec = state.keys.find(k => k.key === row.key)
    if (!spec) continue
    const localTs = state.meta[row.key] || 0
    if (!(row.updated_at > localTs)) continue          // local is same or newer
    const data = unwrap(row.value)
    if (data === undefined) continue
    const json = JSON.stringify(data)
    try { localStorage.setItem(spec.storageKey, json) } catch {}
    state.meta[row.key] = row.updated_at
    state.lastApplied[row.key] = json                  // echo guard
    saveMeta()
    state.onRemote?.(row.key, data)
  }
}

/**
 * Start syncing. `keys` maps sync keys to their localStorage keys;
 * `onRemote(key, data)` is how newer server values reach React state.
 * Pull on start, on tab-visible, and on network regain — the pattern that
 * makes desktop→phone feel instant without holding a socket open.
 */
export function initSettingsSync({ keys, onRemote }) {
  if (state.started) return () => {}
  state.started = true
  state.keys = keys
  state.onRemote = onRemote
  loadMeta()
  pull()
  const onVis = () => { if (document.visibilityState === 'visible') pull() }
  window.addEventListener('visibilitychange', onVis)
  window.addEventListener('online', pull)
  return () => {
    window.removeEventListener('visibilitychange', onVis)
    window.removeEventListener('online', pull)
    state.started = false
  }
}